import React, { useState } from 'react';
import { Box, Flex, TextField } from '@radix-ui/themes';
import * as Label from '@radix-ui/react-label';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Footer } from '../../../layout/footer/Footer';
import useLanguage from '../../../contexts/language/LanguageContext';
import { Page, TransitionUrl, TranslationPath } from '../../../contexts/kioskConfiguration/type';
import { RootState } from '../../../store/store';
import { sendAuthNotification } from '../../../apollo/mutations/sendAuthNotification';
import { setNewPin, setTransportationForm } from '../../../store/slices/transportation/transportationSlice';
import { getTranslationByPath } from '../../../utils/translations';
import { isEmailValid, isPhoneNumberValid } from '../../../utils/validators';
import { addNotification } from '../../../store/slices/notification/notificationSlice';
import { MainContent } from '../../../layout/content/MainContent';
import { KeyboardWrapper } from '../../../components/keyboard/KeyboardWrapper';


export type PhoneNumberCheckTemplateType = {
  transitionUrl: TransitionUrl
  phoneNumberInputLabel: TranslationPath
  phoneNumberInputPlaceholder: TranslationPath
  emailInputLabel: TranslationPath
  emailInputPlaceholder: TranslationPath
  invalidPhoneNumberText: TranslationPath
  invalidEmailText: TranslationPath
  sendNotificationErrorText: TranslationPath
}

type Props = {
  page: Page<PhoneNumberCheckTemplateType>
}


export const PhoneNumberCheckTemplate = (props: Props): React.ReactElement => {
  const [isProcessing, setIsProcessing] = React.useState(false);
  const fetchedTransportation = useSelector((state: RootState) => state.transportation.fetchedTransportation)
  const transportationForm = useSelector((state: RootState) => state.transportation.transportationForm)
  const newPin = useSelector((state: RootState) => state.transportation.newPin)

  const language = useLanguage();
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const onContinue = async () => {
    if (!isPhoneNumberValid(transportationForm.phoneNumber)) {
      dispatch(addNotification(
        { type: 'error', title: getTranslationByPath(language.translations, props.page.data.invalidPhoneNumberText) },
      ))
      return
    }
    setIsProcessing(true)

    try {
      const result = await sendAuthNotification({
        newPin,
        phoneNumber: transportationForm.phoneNumber,
        qtirLanguageId: language.qtirLanguageId,
        transportationId: fetchedTransportation ? fetchedTransportation.id : undefined,
      })
      if (result.data?.sendAuthNotification) {
        dispatch(setNewPin(false))
        navigate(props.page.data.transitionUrl)
      } else {
        dispatch(addNotification(
          { type: 'error', title: getTranslationByPath(language.translations, props.page.data.sendNotificationErrorText) },
        ))
        setIsProcessing(false)
      }
    } catch (e) {
      dispatch(addNotification(
        { type: 'error', title: getTranslationByPath(language.translations, props.page.data.sendNotificationErrorText) },
      ))
      setIsProcessing(false)
    }
  }

  return (
    <>
      <MainContent>
        <Flex className="flex-col justify-center items-center w-full mt-10">
          <Flex direction="column" gap="1" width="100%" className="items-center">
            <Box maxWidth="500px" className="w-full">
              <Label.Root className="LabelRoot text-2xl mb-10" htmlFor="identification">
                {getTranslationByPath(language.translations, props.page.data.phoneNumberInputLabel)}
              </Label.Root>
              <KeyboardWrapper layout="numeric" title={getTranslationByPath(language.translations, props.page.data.phoneNumberInputLabel)}>
                <TextField.Root
                  style={{ height: '50px' }}
                  className="mt-3"
                  placeholder={getTranslationByPath(language.translations, props.page.data.phoneNumberInputPlaceholder)}
                  value={transportationForm.phoneNumber}
                  onChange={(e) => {
                    dispatch(setTransportationForm({
                      ...transportationForm,
                      phoneNumber: e.target.value,
                    }))
                  }}
                />
              </KeyboardWrapper>
            </Box>
          </Flex>
        </Flex>
      </MainContent>

      <Footer backUrl={props.page.footer?.back} onContinue={onContinue} disabledBackBtn={isProcessing} disabledContinueBtn={isProcessing} />
    </>
  )
}
